import { FC, useMemo } from 'react';
import {
  tAnswer,
  tLearnTaskType,
  useLearnTaskActions,
  lessonSelectors
} from 'common/modules/ReactShared';
import {
  DEFAULT_LESSON_BACKGROUND,
  getBackgroundColorIfLoaded
} from 'students/views/pages/Lessons/Lesson/LessonPage/helpers';
import Page from 'students/views/pages/Page';
import { useTypedSelector } from 'students/stores/_utils';
import bgLeft from 'students/views/shared/assets/bgLightBubbles/bgLightBubbleLeft.svg';
import bgRight from 'students/views/shared/assets/bgLightBubbles/bgLightBubbleRight.svg';
import { SpinnerBlock } from 'students/views/shared/components/Spinner';
import { isLightFontNeeded } from 'students/views/shared/helpers';
import styled from 'styled-components';

import LessonTaskTitle from '../../LessonTask/LessonTaskTitle';
import NotSupportedTask from '../../NotSupportedTask';
import { tSubjectComponentProps } from '../types';
import { learnTaskMap } from './Tasks';

export interface ILearnSubjectComponentProps extends tSubjectComponentProps {
  task: tLearnTaskType;
}

const LearnTaskContainer: FC<ILearnSubjectComponentProps> = ({
  lesson,
  task,
  taskSession,
  isLoading,
  onFinishAndNext,
  isCompleting
}) => {
  const { onFinish } = useLearnTaskActions({
    lessonTaskSession: taskSession ?? null,
    isCompleting: isCompleting ?? false,
    onFinishAndNext(answers?: tAnswer): Promise<void> {
      return onFinishAndNext(answers) as Promise<void>;
    }
  });

  const { gradient, background } = getBackgroundColorIfLoaded(
    lesson?.color || null,
    !!lesson
  );
  const lessonSession = useTypedSelector(lessonSelectors.selectLessonSession);
  const isLightFont = isLightFontNeeded(background);
  const TaskComponent = learnTaskMap.get(task.type);
  const previousTaskId = useMemo(() => {
    const teachTaskIds = lessonSession?.summary?.teach?.tasks.map(({ id }) => id);
    const currentTaskId = taskSession?.taskId ?? task.id;

    if (!teachTaskIds?.length || !currentTaskId) {
      return null;
    }

    const currentTaskIndex = teachTaskIds.indexOf(currentTaskId);

    if (currentTaskIndex <= 0) {
      return null;
    }

    return teachTaskIds[currentTaskIndex - 1];
  }, [lessonSession?.summary?.teach?.tasks, task.id, taskSession?.taskId]);

  const taskBody = useMemo(() => {
    let currentTaskBody: JSX.Element | null = null;
    if (isLoading) {
      currentTaskBody = (
        <SSpinnerWrapper>
          <SpinnerBlock />
        </SSpinnerWrapper>
      );
    } else {
      currentTaskBody = TaskComponent ? (
        <TaskComponent
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          task={task}
          onFinish={onFinish}
          lightFont={isLightFont}
          isCompleting={!!isCompleting}
          previousTaskId={previousTaskId}
        />
      ) : (
        <NotSupportedTask
          onNext={onFinish}
          color={lesson?.color || DEFAULT_LESSON_BACKGROUND}
          isCompleting={!!isCompleting}
        />
      );
    }

    return currentTaskBody;
  }, [
    isCompleting,
    isLoading,
    TaskComponent,
    task,
    isLightFont,
    onFinish,
    lesson?.color,
    previousTaskId
  ]);

  const apiBgImageURL = task.backgroundImageURL || lesson?.bgImageURL;
  const hasApiBgImage = !!apiBgImageURL;
  const bgImage = hasApiBgImage
    ? `url(${apiBgImageURL})`
    : `url(${bgLeft}), url(${bgRight})`;
  const bgSize = hasApiBgImage ? 'cover' : undefined;
  const bgPosition = hasApiBgImage ? 'center' : undefined;

  return (
    <Page
      background={background}
      gradient={gradient}
      bgImage={bgImage}
      bgSize={bgSize}
      bgPosition={bgPosition}
      hasImageOverlay={hasApiBgImage}
      overlayColor={hasApiBgImage ? lesson?.color || undefined : undefined}
    >
      <LessonTaskTitle title={lesson?.title} />
      {taskBody}
    </Page>
  );
};

export default LearnTaskContainer;

const SSpinnerWrapper = styled.div`
  flex-grow: 1;
  display: grid;
  place-content: center;
`;
